import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useHotkeys } from "react-hotkeys-hook";
import { useShallow } from "zustand/shallow";

// Adjust these imports to your project
import { WORKFLOW_TYPES } from "../constants/flowConfig";
import { useWorkflowStore } from "../stores";

const Keycap = ({ children }) => (
  <kbd className="inline-flex items-center rounded-md border border-slate-300 bg-white/80 px-2 py-0.5 text-xs font-medium shadow-inner">
    {children}
  </kbd>
);

const Row = ({ label, keys, mac }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-slate-700">{label}</span>
    <div className="flex items-center gap-1">
      <div className="hidden md:flex items-center gap-1">
        {keys.map((k, i) => (
          <Keycap key={i}>{k}</Keycap>
        ))}
      </div>
      <div className="md:hidden flex items-center gap-1">
        {(mac || keys).map((k, i) => (
          <Keycap key={i}>{k}</Keycap>
        ))}
      </div>
    </div>
  </div>
);

function useIsEditor() {
  const [currentWorkflow] = useWorkflowStore(useShallow((s) => [s.currentWorkflow]));
  return (
    currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS ||
    currentWorkflow === WORKFLOW_TYPES.MAIN_PROCESS
  );
}

export default function ShortcutsHelp() {
  const isEditor = useIsEditor();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const cardRef = useRef(null);
  const inputRef = useRef(null);

  const SHORTCUTS = [
    { group: "Meta", items: [{ label: "Open / Close Shortcuts", keys: ["Ctrl", "K"], mac: ["⌘", "K"], when: "always" }] },
    {
      group: "Editing",
      items: [
        { label: "Undo", keys: ["Ctrl", "Z"], mac: ["⌘", "Z"], when: "editor" },
        { label: "Redo", keys: ["Ctrl", "Y"], mac: ["⌘", "Y"], when: "editor" },
        { label: "Show/Hide Original Socket", keys: ["Shift", "A"], mac: ["⇧", "A"], when: "editor" },
        { label: "Show/Hide Wireframe", keys: ["W"], mac: ["W"], when: "editor" },
        { label: "Toggle Ghost Mode", keys: ["G"], mac: ["G"], when: "editor" },
        { label: "Toggle Color Mode", keys: ["D"], mac: ["D"], when: "editor" },
        { label: "Toggle Normal Mode", keys: ["Shift", "N"], mac: ["⇧", "N"], when: "editor" },
      ],
    },
  ];

  // Toggle with ctrl/cmd+k (works open or closed)
  useHotkeys(
    "ctrl+k, cmd+k",
    (e) => {
      e.preventDefault();
      setOpen((o) => !o);
    },
    { enableOnFormTags: true },
    [setOpen]
  );

  // Close with Esc
  useHotkeys(
    "esc",
    (e) => {
      if (!open) return;
      e.preventDefault();
      setOpen(false);
    },
    { enabled: open, enableOnFormTags: true }
  );

  // Focus search when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [open]);

  // Block interactions OUTSIDE the modal only (so typing/scrolling inside works)
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const isInsideCard = (target) =>
      cardRef.current && (cardRef.current === target || cardRef.current.contains(target));

    const onWheel = (e) => {
      if (isInsideCard(e.target)) return; // allow scrolling inside
      e.preventDefault();
      e.stopPropagation();
    };

    const onPointer = (e) => {
      if (isInsideCard(e.target)) return; // allow clicks inside
      e.stopPropagation();
    };

    const onKeydown = (e) => {
      const isToggleK = e.key && e.key.toLowerCase() === "k" && (e.ctrlKey || e.metaKey);
      const isEsc = e.key === "Escape";
      if (isToggleK || isEsc) return; // let these through everywhere
      if (isInsideCard(e.target)) return; // allow typing inside modal
      e.stopPropagation();
      e.preventDefault();
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("pointerdown", onPointer, true);
    window.addEventListener("keydown", onKeydown, true);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("pointerdown", onPointer, true);
      window.removeEventListener("keydown", onKeydown, true);
    };
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Search (case-insensitive)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SHORTCUTS.map(({ group, items }) => {
      const base = items.filter((i) => i.when !== "editor" || isEditor);
      const vis = q ? base.filter((i) => i.label.toLowerCase().includes(q)) : base;
      return { group, items: vis };
    }).filter((g) => g.items.length > 0);
  }, [query, isEditor]);

  const modal = (
    <div className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0"}`} />
      {/* Card */}
      <div className="absolute inset-0 flex items-start justify-center pt-24">
        <div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard Shortcuts"
          className={`w-[min(720px,92vw)] max-h-[80vh] rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 transition-all ${
            open ? "scale-100 opacity-100" : "scale-95 opacity-0"
          } flex flex-col`} 
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h2 className="text-base font-semibold text-slate-800">Keyboard Shortcuts</h2>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="hidden sm:inline">Close</span>
              <Keycap>Esc</Keycap>
            </div>
          </div>

          {/* Body (scrollable) */}
          <div className="flex-1 overflow-y-auto px-6 py-3">
            <div className="mb-3">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search shortcuts…"
                className="w-full rounded-lg border border-slate-300 bg-white/70 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="flex flex-col gap-6">
              {filtered.length === 0 && (
                <div className="text-sm text-slate-500">No shortcuts match “{query}”.</div>
              )}
              {filtered.map(({ group, items }) => (
                <div key={group} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{group}</div>
                  <div className="divide-y divide-slate-200">
                    {items.map((i, idx) => (
                      <Row key={group + idx} label={i.label} keys={i.keys} mac={i.mac || i.keys} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setOpen(false)}
                className="inline-flex items-center rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if(!open) return null;
  return createPortal(modal, document.body);
}